// src/pages/dashboard/PoliciesSettings.js
// Settings → Policies page. Migrated from src/components/site-editor/panels/PoliciesPanel.js
// in Phase 2 of the IA restructure (see docs/DF-IA-Restructure-Spec-May2026.md, Decision 2).
//
// Reads/writes via the canonical Firestore location practitioners/{uid}.policies.{cancellation|privacy|terms}
// through src/services/policies-service.js. On first load, opportunistically migrates from the legacy
// nested location at website_published.contactFooter.{cancellationPolicy|privacyPolicy|termsOfService}.
//
// The original sub-components (CancellationPolicyBuilder, PrivacyPolicyBuilder, etc.) are preserved
// verbatim per the spec's "move file wholesale, refactor in a follow-up" approach. They call
// useWebsiteBuilder() which now resolves to a local React context mirroring the original
// { state, actions } shape — keeps sub-component bodies unchanged.

import React, { useState, useEffect, useContext, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { FileText, AlertCircle, CheckCircle, Circle, ChevronRight } from 'lucide-react';
import { auth, db } from '../../config/firebase-config';
import { doc, getDoc } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import { savePolicy, migrateLegacyPolicies } from '../../services/policies-service';
import InlineCallout from '../../components/site-editor/InlineCallout';
import PolicyDisclaimer from '../../components/policies/PolicyDisclaimer';
import PolicyEditor from '../../components/policies/PolicyEditor';
import Toggle from '../../components/Toggle';
import LoadingButton from '../../components/common/LoadingButton';
import { useToast } from '../../components/ToastNotification';
import { useUnsavedChangesGuard } from '../../hooks/useUnsavedChangesGuard';

// Maps legacy contactFooter field name → canonical policy name (used by updateContactFooter shim).
const FIELD_TO_POLICY = {
  cancellationPolicy: 'cancellation',
  privacyPolicy: 'privacy',
  termsOfService: 'terms',
};

// Local React context mirroring useWebsiteBuilder's { state, actions } shape.
// Sub-components below call useWebsiteBuilder() and read this — keeps their bodies unchanged.
const PoliciesContext = React.createContext(null);
const useWebsiteBuilder = () => useContext(PoliciesContext);

// ============================================================================
// PAGE WRAPPER — auth, data loading, opportunistic migration, page chrome
// ============================================================================

const PoliciesSettings = () => {
  const [uid, setUid] = useState(null);
  const [practData, setPractData] = useState(null);
  const [policiesData, setPoliciesData] = useState({});
  const [loading, setLoading] = useState(true);
  const [migrating, setMigrating] = useState(false);

  useEffect(() => {
    let done = false;
    const timeout = setTimeout(() => {
      if (!done) { done = true; setLoading(false); }
    }, 5000);
    const unsub = onAuthStateChanged(auth, async (user) => {
      if (done) return;
      if (!user) return; // wait for real user — Firebase fires null first on cold start
      done = true;
      clearTimeout(timeout);
      setUid(user.uid);

      try {
        const snap = await getDoc(doc(db, 'practitioners', user.uid));
        const data = snap.exists() ? snap.data() : {};
        setPractData(data);

        let p = data.policies || null;
        if (!p) {
          setMigrating(true);
          try {
            const result = await migrateLegacyPolicies(user.uid);
            if (result.errors.length > 0) {
              console.warn('[policies] Migration errors:', result.errors);
            }
            if (result.migrated.length > 0) {
              const refreshSnap = await getDoc(doc(db, 'practitioners', user.uid));
              p = refreshSnap.data()?.policies || {};
              console.log('[policies] Migrated from legacy:', result.migrated);
            }
          } catch (err) {
            console.error('[policies] Migration crashed:', err);
            // Allow user to continue with empty state — they can still create policies fresh.
          } finally {
            setMigrating(false);
          }
        }
        setPoliciesData(p || {});
      } catch (err) {
        console.error('[policies] Load failed:', err);
      } finally {
        setLoading(false);
      }
    });
    return () => { done = true; clearTimeout(timeout); unsub(); };
  }, []);

  // Awaitable explicit-save helper. Returns the savePolicy promise so each
  // builder/editor can dispatch its own toast.success/error feedback. Optimistic
  // local update so the UI reflects the change immediately. Caller (Save handler)
  // decides what feedback to show. Reference is stable across policiesData
  // changes — see PR #13 history for the doom-loop fix this preserves.
  const updateContactFooter = useCallback((field, value) => {
    const policyName = FIELD_TO_POLICY[field];
    if (!policyName || !uid) {
      if (!policyName) console.warn('[PoliciesSettings] updateContactFooter called with unknown field:', field);
      return Promise.resolve(); // No-op: caller still gets a settled promise
    }
    setPoliciesData(prev => ({ ...prev, [policyName]: value }));
    return savePolicy(uid, policyName, value);
  }, [uid]);

  // State derived from policiesData and practData. Recomputes when those change.
  const state = useMemo(() => {
    const wp = practData?.website_published || {};
    return {
      draft: {
        contactFooter: {
          cancellationPolicy: policiesData.cancellation || null,
          privacyPolicy: policiesData.privacy || null,
          termsOfService: policiesData.terms || null,
          address: wp.contactFooter?.address || {},
          email: wp.contactFooter?.email || auth?.currentUser?.email || '',
        },
        profile: {
          displayName: wp.profile?.displayName || auth?.currentUser?.displayName || '',
        },
        content: {
          services: wp.content?.services || [],
        },
      },
    };
  }, [policiesData, practData]);

  // Actions are stable across state changes — only re-memo when the underlying
  // callback identity changes (i.e., when uid or toast change).
  const actions = useMemo(() => ({
    updateContactFooter,
  }), [updateContactFooter]);

  // Combined contextValue for the Provider. Mirrors the original
  // useWebsiteBuilder { state, actions } shape so sub-components are unchanged.
  const contextValue = useMemo(() => ({ state, actions }), [state, actions]);

  return (
    <div className="min-h-screen bg-gray-50 pt-20">
      <div className="max-w-4xl mx-auto px-4 py-6">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 text-sm mb-6" aria-label="Breadcrumb">
          <Link to="/dashboard/practitioner" className="text-purple-600 hover:text-purple-700 font-medium transition-colors">
            Dashboard
          </Link>
          <span className="text-gray-400">/</span>
          <Link to="/dashboard/settings" className="text-purple-600 hover:text-purple-700 font-medium transition-colors">
            Settings
          </Link>
          <span className="text-gray-400">/</span>
          <span className="text-gray-900 font-medium">Policies</span>
        </nav>

        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Policies</h1>
          <p className="text-gray-600">Cancellation, privacy, and terms of service</p>
        </div>

        {/* Content */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          {loading ? (
            <div className="p-12 text-center text-sm text-gray-500">Loading your policies…</div>
          ) : migrating ? (
            <div className="p-12 text-center text-sm text-gray-500">Importing your existing policies…</div>
          ) : (
            <PoliciesContext.Provider value={contextValue}>
              <PoliciesPanel />
            </PoliciesContext.Provider>
          )}
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// ORIGINAL POLICIES PANEL (preserved verbatim — sub-components below also unchanged)
// ============================================================================

const PoliciesPanel = () => {
  const { state } = useWebsiteBuilder();
  const [activePolicy, setActivePolicy] = useState(null); // null, 'cancellation', 'privacy', 'terms'

  // Three wrapper-level dirty states — one per builder. Only one builder is
  // mounted at a time, so at most one of these is true. On builder unmount, the
  // flush-on-unmount cleanup in the propagation useEffect resets the relevant
  // state to false (matches current "switching builders is implicit discard" UX).
  // See DEF-036 for context.
  const [isCancellationDirty, setIsCancellationDirty] = useState(false);
  const [isPrivacyDirty, setIsPrivacyDirty] = useState(false);
  const [isTermsDirty, setIsTermsDirty] = useState(false);

  const isAnyBuilderDirty = isCancellationDirty || isPrivacyDirty || isTermsDirty;

  // Register beforeunload guard for tab close / refresh / browser back-forward
  // while any builder has unsaved changes. In-app <Link>/navigate() paths are
  // unguarded — see DEF-038 (blocked on data-router migration).
  useUnsavedChangesGuard(isAnyBuilderDirty);

  // Check which policies exist
  const hasCancellationPolicy = !!state.draft.contactFooter.cancellationPolicy;
  const hasPrivacyPolicy = !!state.draft.contactFooter.privacyPolicy?.content;
  const hasTermsOfService = !!state.draft.contactFooter.termsOfService?.content;

  // If viewing a specific policy, render its builder
  if (activePolicy === 'cancellation') {
    return <CancellationPolicyBuilder onBack={() => setActivePolicy(null)} onDirtyChange={setIsCancellationDirty} />;
  }

  if (activePolicy === 'privacy') {
    return <PrivacyPolicyBuilder onBack={() => setActivePolicy(null)} onDirtyChange={setIsPrivacyDirty} />;
  }

  if (activePolicy === 'terms') {
    return <TermsOfServiceBuilder onBack={() => setActivePolicy(null)} onDirtyChange={setIsTermsDirty} />;
  }

  // Default view: Policy cards list
  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Policies</h3>
        <p className="text-sm text-gray-600">
          Protect your practice and set clear expectations with your clients.
        </p>
      </div>

      {/* Playbook Callout */}
      <InlineCallout
        text="Having clear policies builds trust. Even simple policies show professionalism."
        playbookTopic="policies-and-protection"
      />

      {/* Legal Disclaimer */}
      <PolicyDisclaimer />

      {/* Policy Cards */}
      <div className="space-y-4">
        {/* Cancellation Policy Card */}
        <PolicyCard
          title="Cancellation Policy"
          isCreated={hasCancellationPolicy}
          previewText={hasCancellationPolicy ? "24-hour notice required for refunds..." : null}
          description="Define your cancellation timeframe and refund policy"
          onClick={() => setActivePolicy('cancellation')}
        />

        {/* Privacy Policy Card */}
        <PolicyCard
          title="Privacy Policy"
          isCreated={hasPrivacyPolicy}
          previewText={hasPrivacyPolicy ? "Your privacy is important to us. This Privacy Policy explains..." : null}
          description="Explain how you handle client data"
          onClick={() => setActivePolicy('privacy')}
        />

        {/* Terms of Service Card */}
        <PolicyCard
          title="Terms of Service"
          isCreated={hasTermsOfService}
          previewText={hasTermsOfService ? "By booking a session, you agree to these terms..." : null}
          description="Define expectations for your services"
          onClick={() => setActivePolicy('terms')}
        />
      </div>

      {/* Footer Note */}
      <div className="mt-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
        <p className="text-xs text-gray-600 leading-relaxed">
          <strong className="font-semibold text-gray-900">Footer Integration:</strong> Policy links automatically appear
          in your website footer once created. No extra setup needed.
        </p>
      </div>
    </div>
  );
};

// Policy Card Component
const PolicyCard = ({ title, isCreated, previewText, description, onClick }) => {
  return (
    <button
      onClick={onClick}
      className={`w-full text-left p-5 rounded-lg border-2 transition-all hover:border-purple-300 hover:shadow-md ${
        isCreated
          ? 'bg-white border-gray-200'
          : 'bg-white border-dashed border-gray-300'
      }`}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3 flex-1">
          {/* Status Icon */}
          {isCreated ? (
            <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
          ) : (
            <Circle className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" />
          )}

          {/* Content */}
          <div className="flex-1">
            <h4 className={`text-sm font-semibold mb-1 ${isCreated ? 'text-gray-900' : 'text-gray-700'}`}>
              {title}
            </h4>
            {isCreated && previewText ? (
              <p className="text-xs text-gray-600 line-clamp-1">
                {previewText}
              </p>
            ) : (
              <p className="text-xs text-gray-500">
                {description}
              </p>
            )}
          </div>
        </div>

        {/* Action Link */}
        <div className="flex items-center gap-1 text-purple-600 flex-shrink-0">
          <span className="text-sm font-medium">
            {isCreated ? 'Edit' : 'Set Up'}
          </span>
          <ChevronRight className="w-4 h-4" />
        </div>
      </div>
    </button>
  );
};

// ============================================================================
// CANCELLATION POLICY BUILDER (EXISTING - UNCHANGED)
// ============================================================================

const CancellationPolicyBuilder = ({ onBack, onDirtyChange }) => {
  const { state, actions } = useWebsiteBuilder();
  const cancellationPolicy = state.draft.contactFooter.cancellationPolicy || {
    timeframe: '24h',
    customTimeframe: '',
    refundPercentage: 'full',
    customRefund: '',
    noShowPolicy: ''
  };

  const [timeframe, setTimeframe] = useState(cancellationPolicy.timeframe || '24h');
  const [customTimeframe, setCustomTimeframe] = useState(cancellationPolicy.customTimeframe || '');
  const [refundPercentage, setRefundPercentage] = useState(cancellationPolicy.refundPercentage || 'full');
  const [customRefund, setCustomRefund] = useState(cancellationPolicy.customRefund || '');
  const [noShowPolicy, setNoShowPolicy] = useState(cancellationPolicy.noShowPolicy || '');
  const [errors, setErrors] = useState({});
  const toast = useToast();
  const [isDirty, setIsDirty] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Propagate local isDirty up to PoliciesPanel so the wrapper-level beforeunload
  // guard sees this builder's state. See DEF-036.
  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty, onDirtyChange]);

  // Flush-on-unmount: when this builder unmounts (user switches activePolicy or
  // navigates away), reset the wrapper's tracked dirty state to false. Matches
  // the existing "switching builders is implicit discard" UX.
  useEffect(() => {
    return () => onDirtyChange?.(false);
  }, [onDirtyChange]);

  const handleSave = async () => {
    const policy = {
      timeframe,
      customTimeframe: timeframe === 'custom' ? customTimeframe : '',
      refundPercentage,
      customRefund: refundPercentage === 'custom' ? customRefund : '',
      noShowPolicy,
    };

    // Validate before saving
    if (timeframe === 'custom' && !customTimeframe) {
      setErrors(prev => ({ ...prev, customTimeframe: 'Enter a custom timeframe to save your policy' }));
      return;
    }
    if (refundPercentage === 'custom' && !customRefund) {
      setErrors(prev => ({ ...prev, customRefund: 'Enter a custom refund amount to save your policy' }));
      return;
    }

    // Clear validation errors on valid submit
    setErrors(prev => ({ ...prev, customTimeframe: null, customRefund: null }));

    setIsSaving(true);
    try {
      await actions.updateContactFooter('cancellationPolicy', policy);
      setIsDirty(false);
      toast.success('Saved', 'Cancellation policy saved.');
    } catch (err) {
      console.error('[policies] Cancellation save failed:', err);
      toast.error('Save failed', 'Your cancellation policy could not be saved. Please check your connection and try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleBack = () => {
    if (isDirty) {
      const confirmed = window.confirm('You have unsaved changes. Discard?');
      if (!confirmed) return;
    }
    onBack();
  };

  const timeframeOptions = [
    { value: '24h', label: '24 hours', display: '24 hours' },
    { value: '48h', label: '48 hours', display: '48 hours' },
    { value: '72h', label: '72 hours', display: '72 hours' },
    { value: '1week', label: '1 week', display: 'one week' },
    { value: 'custom', label: 'Custom', display: '' }
  ];

  const refundOptions = [
    { value: 'full', label: 'Full refund (100%)', display: 'full refund' },
    { value: '50', label: '50% refund', display: '50% refund' },
    { value: 'none', label: 'No refund', display: 'no refund' },
    { value: 'custom', label: 'Custom', display: '' }
  ];

  const handleTimeframeChange = (value) => {
    setTimeframe(value);
    setIsDirty(true);
    if (value !== 'custom') {
      setCustomTimeframe('');
      setErrors({ ...errors, customTimeframe: null });
    }
  };

  const handleRefundChange = (value) => {
    setRefundPercentage(value);
    setIsDirty(true);
    if (value !== 'custom') {
      setCustomRefund('');
      setErrors({ ...errors, customRefund: null });
    }
  };

  const handleCustomTimeframeChange = (value) => {
    setCustomTimeframe(value);
    setIsDirty(true);
    if (value.trim()) {
      setErrors({ ...errors, customTimeframe: null });
    }
  };

  const handleCustomRefundChange = (value) => {
    setCustomRefund(value);
    setIsDirty(true);
    if (value.trim()) {
      setErrors({ ...errors, customRefund: null });
    }
  };

  // Generate policy preview text
  const generatePolicyText = () => {
    let timeframeText = '';
    if (timeframe === 'custom') {
      timeframeText = customTimeframe || '[custom timeframe]';
    } else {
      const option = timeframeOptions.find(opt => opt.value === timeframe);
      timeframeText = option?.display || '24 hours';
    }

    let refundText = '';
    if (refundPercentage === 'custom') {
      refundText = customRefund || '[custom refund policy]';
    } else {
      const option = refundOptions.find(opt => opt.value === refundPercentage);
      refundText = option?.display || 'full refund';
    }

    let policyText = `**Cancellation Policy**\n\n`;
    policyText += `If you need to cancel your appointment, please do so at least ${timeframeText} in advance to receive a ${refundText}.\n\n`;
    policyText += `Cancellations made within ${timeframeText} of your scheduled appointment may not be eligible for a refund.`;

    if (noShowPolicy.trim()) {
      policyText += `\n\n**No-Show Policy**\n\n${noShowPolicy}`;
    }

    return policyText;
  };

  return (
    <div className="p-6 space-y-6">
      {/* Back Button */}
      <button
        onClick={handleBack}
        className="flex items-center gap-2 text-sm text-gray-600 hover:text-purple-600 transition-colors"
      >
        <ChevronRight className="w-4 h-4 rotate-180" />
        <span>Back to Policies</span>
      </button>

      {/* Header */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Cancellation Policy</h3>
        <p className="text-sm text-gray-600">
          Set clear expectations about cancellations and refunds. This helps protect your time and builds trust with clients.
        </p>
      </div>

      {/* Inline Callout */}
      <InlineCallout
        text="A clear cancellation policy protects both you and your clients. Learn best practices for setting fair boundaries."
        playbookTopic="policies-and-protection"
      />

      {/* Cancellation Timeframe */}
      <div className="space-y-3">
        <label className="block text-sm font-medium text-gray-900">
          Cancellation Timeframe
        </label>
        <p className="text-xs text-gray-600 mb-2">
          How far in advance must clients cancel to receive a refund?
        </p>
        <div className="grid grid-cols-2 gap-3">
          {timeframeOptions.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => handleTimeframeChange(option.value)}
              className={`p-3 rounded-lg border-2 transition-all text-left ${
                timeframe === option.value
                  ? 'border-purple-600 bg-purple-50'
                  : 'border-gray-200 hover:border-purple-300'
              }`}
            >
              <p className="text-sm font-medium text-gray-900">{option.label}</p>
            </button>
          ))}
        </div>

        {timeframe === 'custom' && (
          <div className="pt-2">
            <input
              type="text"
              value={customTimeframe}
              onChange={(e) => handleCustomTimeframeChange(e.target.value)}
              placeholder="e.g., 3 days, 5 hours"
              className={`w-full px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent ${
                errors.customTimeframe ? 'border-red-400' : 'border-gray-300'
              }`}
            />
            {errors.customTimeframe && (
              <p className="mt-1 text-xs text-red-600 flex items-center gap-1">
                <AlertCircle className="w-3 h-3" />
                {errors.customTimeframe}
              </p>
            )}
          </div>
        )}
      </div>

      {/* Refund Percentage */}
      <div className="space-y-3">
        <label className="block text-sm font-medium text-gray-900">
          Refund Amount
        </label>
        <p className="text-xs text-gray-600 mb-2">
          What refund will clients receive if they cancel within the timeframe?
        </p>
        <div className="grid grid-cols-2 gap-3">
          {refundOptions.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => handleRefundChange(option.value)}
              className={`p-3 rounded-lg border-2 transition-all text-left ${
                refundPercentage === option.value
                  ? 'border-purple-600 bg-purple-50'
                  : 'border-gray-200 hover:border-purple-300'
              }`}
            >
              <p className="text-sm font-medium text-gray-900">{option.label}</p>
            </button>
          ))}
        </div>

        {refundPercentage === 'custom' && (
          <div className="pt-2">
            <input
              type="text"
              value={customRefund}
              onChange={(e) => handleCustomRefundChange(e.target.value)}
              placeholder="e.g., 75% refund, store credit only"
              className={`w-full px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent ${
                errors.customRefund ? 'border-red-400' : 'border-gray-300'
              }`}
            />
            {errors.customRefund && (
              <p className="mt-1 text-xs text-red-600 flex items-center gap-1">
                <AlertCircle className="w-3 h-3" />
                {errors.customRefund}
              </p>
            )}
          </div>
        )}
      </div>

      {/* No-Show Policy */}
      <div className="space-y-3">
        <label className="block text-sm font-medium text-gray-900">
          No-Show Policy <span className="text-xs font-normal text-gray-500">(Optional)</span>
        </label>
        <p className="text-xs text-gray-600 mb-2">
          What happens if a client doesn't show up for their appointment?
        </p>
        <textarea
          value={noShowPolicy}
          onChange={(e) => { setNoShowPolicy(e.target.value); setIsDirty(true); }}
          placeholder="e.g., No-shows will be charged the full session fee. Please notify us if you need to cancel."
          rows={3}
          maxLength={300}
          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
        />
        <div className="flex justify-between">
          <p className="text-xs text-gray-500">
            Clear no-show policies reduce lost revenue
          </p>
          <p className="text-xs text-gray-500">
            {noShowPolicy.length}/300
          </p>
        </div>
      </div>

      {/* Policy Preview */}
      <div className="border border-purple-200 rounded-lg p-5 bg-purple-50/30 space-y-3">
        <div className="flex items-center gap-2">
          <FileText className="w-4 h-4 text-purple-600" />
          <h4 className="text-sm font-semibold text-gray-900">Policy Preview</h4>
        </div>
        <div className="p-4 bg-white rounded-lg border border-gray-200">
          <div
            className="text-sm text-gray-700 whitespace-pre-wrap prose prose-sm max-w-none"
            style={{ lineHeight: '1.6' }}
          >
            {generatePolicyText().split('\n').map((line, index) => {
              if (line.startsWith('**') && line.endsWith('**')) {
                // Bold headings
                return (
                  <p key={index} className="font-bold text-gray-900 mb-2">
                    {line.replace(/\*\*/g, '')}
                  </p>
                );
              } else if (line.trim() === '') {
                return <br key={index} />;
              } else {
                return (
                  <p key={index} className="mb-2">
                    {line}
                  </p>
                );
              }
            })}
          </div>
        </div>
        <p className="text-xs text-gray-600">
          This policy will be displayed on your booking page and included in confirmation emails.
        </p>
      </div>

      {/* Help Text */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <p className="text-xs text-blue-800">
          <strong>Tip:</strong> Most practitioners use a 24-48 hour cancellation window with a full or 50% refund.
          This gives you time to rebook while being fair to clients with unexpected situations.
        </p>
      </div>

      {/* Save Button */}
      <div className="flex justify-end pt-2">
        <LoadingButton
          onClick={handleSave}
          loading={isSaving}
          loadingText="Saving..."
          disabled={!isDirty}
          className="px-6 py-2.5 bg-purple-600 text-white font-semibold rounded-lg hover:bg-purple-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
        >
          Save Cancellation Policy
        </LoadingButton>
      </div>
    </div>
  );
};

// ============================================================================
// PRIVACY POLICY BUILDER (FULL IMPLEMENTATION)
// ============================================================================

const PrivacyPolicyBuilder = ({ onBack, onDirtyChange }) => {
  const { state } = useWebsiteBuilder();
  const privacyPolicy = state.draft.contactFooter.privacyPolicy;

  // Check if Stripe is connected
  const stripeConnectData = localStorage.getItem('practitioner_stripe_connect');
  const isStripeConnected = !!stripeConnectData;

  // Determine if we should show editor (policy exists) or question flow (no policy yet)
  const hasExistingPolicy = privacyPolicy?.content;

  if (hasExistingPolicy) {
    return <PrivacyPolicyEditor onBack={onBack} onDirtyChange={onDirtyChange} />;
  } else {
    return <PrivacyPolicyQuestionFlow onBack={onBack} isStripeConnected={isStripeConnected} />;
  }
};

// Question Flow Component
const PrivacyPolicyQuestionFlow = ({ onBack, isStripeConnected }) => {
  const { state, actions } = useWebsiteBuilder();
  const toast = useToast();

  // Auto-detect values
  const autoLocation = state.draft.contactFooter.address?.city && state.draft.contactFooter.address?.state
    ? `${state.draft.contactFooter.address.city}, ${state.draft.contactFooter.address.state}`
    : '';

  // Initialize answers with auto-detected values
  const [answers, setAnswers] = useState({
    collectNamesEmails: true, // Always true
    collectIntakeForms: false,
    collectHealthHistory: false,
    thirdPartyServices: isStripeConnected ? ['stripe'] : [],
    useAnalytics: false,
    businessLocation: autoLocation,
    workWithMinors: false,
  });

  const handleToggle = (id) => {
    setAnswers(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const handleCheckboxChange = (id, optionId, checked) => {
    setAnswers(prev => ({
      ...prev,
      [id]: checked
        ? [...prev[id], optionId]
        : prev[id].filter(item => item !== optionId)
    }));
  };

  const handleTextChange = (id, value) => {
    setAnswers(prev => ({ ...prev, [id]: value }));
  };

  // Generate is itself an explicit user action (single button click), so save once here.
  // The user then reviews/edits in the editor view and clicks Save again to persist edits.
  const handleGenerate = async () => {
    const { generatePrivacyPolicy } = require('../../utils/policyTemplates');

    const practitionerData = {
      displayName: state.draft.profile.displayName,
      email: state.draft.contactFooter.email,
    };

    const generatedContent = generatePrivacyPolicy(answers, practitionerData);

    try {
      await actions.updateContactFooter('privacyPolicy', {
        answers,
        content: generatedContent,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      });
      toast.success('Generated', 'Privacy policy generated. Review and edit before publishing.');
    } catch (err) {
      console.error('[policies] Privacy generate failed:', err);
      toast.error('Generation failed', 'Could not save the generated policy. Please try again.');
    }
  };

  return (
    <div className="p-6 space-y-6">
      {/* Back Button */}
      <button
        onClick={onBack}
        className="flex items-center gap-2 text-sm text-gray-600 hover:text-purple-600 transition-colors"
      >
        <ChevronRight className="w-4 h-4 rotate-180" />
        <span>Back to Policies</span>
      </button>

      {/* Header */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Privacy Policy</h3>
        <p className="text-sm text-gray-600">
          Answer a few questions about your practice and we'll generate a Privacy Policy template you can customize.
        </p>
      </div>

      {/* Disclaimer */}
      <PolicyDisclaimer />

      {/* Questions */}
      <div className="space-y-4">
        {/* Q1: Collect Names & Emails */}
        <QuestionCard
          question="Do you collect client names and email addresses?"
          helperText="Through bookings, contact forms, or newsletter signups"
          autoDetect={true}
          autoDetectReason="Auto-detected: Required for bookings and contact"
        >
          <Toggle
            enabled={answers.collectNamesEmails}
            onChange={() => handleToggle('collectNamesEmails')}
          />
        </QuestionCard>

        {/* Q2: Intake Forms */}
        <QuestionCard
          question="Do you collect intake form information?"
          helperText="Pre-session questionnaires about goals, concerns, or background"
        >
          <Toggle
            enabled={answers.collectIntakeForms}
            onChange={() => handleToggle('collectIntakeForms')}
          />
        </QuestionCard>

        {/* Q3: Health History */}
        <QuestionCard
          question="Do you collect health or personal history?"
          helperText="Important for healers, coaches, and energy workers who ask about medical conditions or personal circumstances"
        >
          <Toggle
            enabled={answers.collectHealthHistory}
            onChange={() => handleToggle('collectHealthHistory')}
          />
        </QuestionCard>

        {/* Q4: Third-Party Services */}
        <QuestionCard
          question="Which third-party services do you use?"
          helperText="We've detected some automatically based on your setup"
          fullWidthControl
        >
          <div className="space-y-2">
            <CheckboxOption
              id="stripe"
              label="Stripe (payment processing)"
              checked={answers.thirdPartyServices.includes('stripe')}
              onChange={(checked) => handleCheckboxChange('thirdPartyServices', 'stripe', checked)}
              autoDetect={isStripeConnected}
              autoDetectReason="Auto-detected: Stripe connected"
            />
            <CheckboxOption
              id="zoom"
              label="Zoom (video sessions)"
              checked={answers.thirdPartyServices.includes('zoom')}
              onChange={(checked) => handleCheckboxChange('thirdPartyServices', 'zoom', checked)}
            />
            <CheckboxOption
              id="google_calendar"
              label="Google Calendar"
              checked={answers.thirdPartyServices.includes('google_calendar')}
              onChange={(checked) => handleCheckboxChange('thirdPartyServices', 'google_calendar', checked)}
            />
            <CheckboxOption
              id="mailchimp"
              label="Email service (Mailchimp, ConvertKit, etc.)"
              checked={answers.thirdPartyServices.includes('mailchimp')}
              onChange={(checked) => handleCheckboxChange('thirdPartyServices', 'mailchimp', checked)}
            />
          </div>
        </QuestionCard>

        {/* Q5: Analytics */}
        <QuestionCard
          question="Do you use analytics on your website?"
          helperText="Google Analytics, Facebook Pixel, or similar tracking tools"
        >
          <Toggle
            enabled={answers.useAnalytics}
            onChange={() => handleToggle('useAnalytics')}
          />
        </QuestionCard>

        {/* Q6: Business Location */}
        <QuestionCard
          question="Where is your business located?"
          helperText="Used for state-specific privacy requirements (e.g., California's CCPA)"
          autoDetect={!!autoLocation}
          autoDetectReason={autoLocation ? `Auto-detected: ${autoLocation}` : ''}
          fullWidthControl
        >
          <input
            type="text"
            value={answers.businessLocation}
            onChange={(e) => handleTextChange('businessLocation', e.target.value)}
            placeholder="e.g., Portland, Oregon"
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          />
        </QuestionCard>

        {/* Q7: Work With Minors */}
        <QuestionCard
          question="Do you work with clients under 18?"
          helperText="Triggers additional privacy protections for children's data (COPPA)"
        >
          <Toggle
            enabled={answers.workWithMinors}
            onChange={() => handleToggle('workWithMinors')}
          />
        </QuestionCard>
      </div>

      {/* Generate Button */}
      <button
        onClick={handleGenerate}
        className="w-full py-3 text-white font-semibold rounded-lg transition-colors"
        style={{ backgroundColor: '#7c3aed' }}
      >
        Generate Privacy Policy
      </button>
    </div>
  );
};

// Editor View Component
const PrivacyPolicyEditor = ({ onBack, onDirtyChange }) => {
  const { state, actions } = useWebsiteBuilder();
  const privacyPolicy = state.draft.contactFooter.privacyPolicy;
  const [showRegenerateConfirm, setShowRegenerateConfirm] = useState(false);
  const [content, setContent] = useState(privacyPolicy?.content || '');
  const toast = useToast();
  const [isDirty, setIsDirty] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Propagate local isDirty up to PoliciesPanel — see DEF-036.
  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty, onDirtyChange]);

  // Flush-on-unmount: reset wrapper's tracked dirty state when this editor unmounts.
  useEffect(() => {
    return () => onDirtyChange?.(false);
  }, [onDirtyChange]);

  const handleContentChange = (html) => {
    setContent(html);
    setIsDirty(true);
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await actions.updateContactFooter('privacyPolicy', {
        ...privacyPolicy,
        content,
        updatedAt: new Date().toISOString(),
      });
      setIsDirty(false);
      toast.success('Saved', 'Privacy policy saved.');
    } catch (err) {
      console.error('[policies] Privacy save failed:', err);
      toast.error('Save failed', 'Your privacy policy could not be saved. Please check your connection and try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleRegenerate = () => {
    // Clear local content; transition back to Q&A flow happens because privacyPolicy.content
    // is empty. Don't save here — user must Save explicitly after re-generating.
    setContent('');
    setIsDirty(false);
    setShowRegenerateConfirm(false);
  };

  const handleBack = () => {
    if (isDirty) {
      const confirmed = window.confirm('You have unsaved changes. Discard?');
      if (!confirmed) return;
    }
    onBack();
  };

  return (
    <div className="p-6 space-y-6">
      {/* Back Button */}
      <button
        onClick={handleBack}
        className="flex items-center gap-2 text-sm text-gray-600 hover:text-purple-600 transition-colors"
      >
        <ChevronRight className="w-4 h-4 rotate-180" />
        <span>Back to Policies</span>
      </button>

      {/* Header */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Privacy Policy</h3>
      </div>

      {/* Disclaimer */}
      <PolicyDisclaimer />

      {/* Editor */}
      <PolicyEditor
        content={content}
        onChange={handleContentChange}
        placeholder="Your privacy policy..."
        minHeight="500px"
      />
      <p className="text-xs text-gray-500 mt-2">
        Edits require clicking Save to persist.
      </p>

      {/* Bottom Actions */}
      <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200">
        <button
          onClick={() => setShowRegenerateConfirm(true)}
          className="text-sm text-gray-600 hover:text-purple-600 transition-colors"
        >
          Regenerate
        </button>
        <LoadingButton
          onClick={handleSave}
          loading={isSaving}
          loadingText="Saving..."
          disabled={!isDirty}
          className="px-6 py-2.5 bg-purple-600 text-white font-semibold rounded-lg hover:bg-purple-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
        >
          Save Privacy Policy
        </LoadingButton>
      </div>

      {/* Regenerate Confirmation Modal */}
      {showRegenerateConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setShowRegenerateConfirm(false)}>
          <div className="bg-white rounded-xl p-6 max-w-md" onClick={(e) => e.stopPropagation()}>
            <h4 className="text-lg font-semibold text-gray-900 mb-2">Regenerate Privacy Policy?</h4>
            <p className="text-sm text-gray-600 mb-6">
              This will replace your current policy with a new template. Your edits will be lost. Continue?
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowRegenerateConfirm(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleRegenerate}
                className="px-4 py-2 text-sm font-medium text-white rounded-lg"
                style={{ backgroundColor: '#7c3aed' }}
              >
                Regenerate
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// HELPER COMPONENTS FOR QUESTION FLOW
// ============================================================================

const QuestionCard = ({ question, helperText, autoDetect, autoDetectReason, children, fullWidthControl }) => {
  return (
    <div className="p-4 border border-gray-200 rounded-lg bg-white">
      {fullWidthControl ? (
        // Stacked layout for text inputs and checkboxes
        <div>
          <h4 className="text-sm font-semibold text-gray-900 mb-1">
            {question}
          </h4>
          <p className="text-xs text-gray-600 mb-3">
            {helperText}
          </p>
          {autoDetect && autoDetectReason && (
            <div className="mb-3 inline-flex items-center gap-1.5 px-2 py-1 bg-green-50 border border-green-200 rounded text-xs text-green-700">
              <CheckCircle className="w-3 h-3" />
              <span>{autoDetectReason}</span>
            </div>
          )}
          {children}
        </div>
      ) : (
        // Side-by-side layout for toggles
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            <h4 className="text-sm font-semibold text-gray-900 mb-1">
              {question}
            </h4>
            <p className="text-xs text-gray-600">
              {helperText}
            </p>
            {autoDetect && autoDetectReason && (
              <div className="mt-2 inline-flex items-center gap-1.5 px-2 py-1 bg-green-50 border border-green-200 rounded text-xs text-green-700">
                <CheckCircle className="w-3 h-3" />
                <span>{autoDetectReason}</span>
              </div>
            )}
          </div>
          <div className="flex-shrink-0 mt-1">
            {children}
          </div>
        </div>
      )}
    </div>
  );
};

const CheckboxOption = ({ id, label, checked, onChange, autoDetect, autoDetectReason }) => {
  return (
    <label className="flex items-center gap-3 cursor-pointer group">
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="w-4 h-4 text-purple-600 border-gray-300 rounded focus:ring-purple-500"
      />
      <div className="flex-1">
        <span className="text-sm text-gray-900 group-hover:text-purple-600 transition-colors">
          {label}
        </span>
        {autoDetect && autoDetectReason && (
          <div className="mt-1 inline-flex items-center gap-1.5 px-2 py-0.5 bg-green-50 border border-green-200 rounded text-xs text-green-700 ml-2">
            <CheckCircle className="w-3 h-3" />
            <span>{autoDetectReason}</span>
          </div>
        )}
      </div>
    </label>
  );
};

// ============================================================================
// TERMS OF SERVICE BUILDER (PLACEHOLDER)
// ============================================================================

const TermsOfServiceBuilder = ({ onBack, onDirtyChange }) => {
  const { state } = useWebsiteBuilder();
  const termsOfService = state.draft.contactFooter.termsOfService;
  const hasExistingTerms = termsOfService?.content;

  if (hasExistingTerms) {
    return <TermsOfServiceEditor onBack={onBack} onDirtyChange={onDirtyChange} />;
  } else {
    return <TermsOfServiceQuestionFlow onBack={onBack} />;
  }
};

// Question Flow Component
const TermsOfServiceQuestionFlow = ({ onBack }) => {
  const { state, actions } = useWebsiteBuilder();
  const toast = useToast();

  // Auto-detect values
  const services = state.draft.content?.services || [];
  const serviceNames = services.filter(s => s.active).map(s => s.name || s.title).filter(Boolean);
  const cancellationPolicy = state.draft.contactFooter.cancellationPolicy;
  const hasCancellationPolicy = !!cancellationPolicy;

  // Initialize answers with auto-detected values
  const [answers, setAnswers] = useState({
    serviceTypes: serviceNames.length > 0 ? serviceNames.join(', ') : '',
    healthMedicalDisclaimer: false,
    financialLegalDisclaimer: false,
    minimumAge: '18+',
    offersPackages: false,
    disputeResolution: 'mediation',
  });

  const handleToggle = (id) => {
    setAnswers(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const handleTextChange = (id, value) => {
    setAnswers(prev => ({ ...prev, [id]: value }));
  };

  const handleSelectChange = (id, value) => {
    setAnswers(prev => ({ ...prev, [id]: value }));
  };

  const getCancellationSummary = (policy) => {
    const timeframeMap = {
      '24h': '24 hours', '48h': '48 hours', '72h': '72 hours',
      '1week': '1 week', 'custom': policy.customTimeframe || 'custom timeframe'
    };
    const refundMap = {
      'full': 'full refund', '50': '50% refund',
      'none': 'no refund', 'custom': policy.customRefund || 'custom refund'
    };
    const timeframe = timeframeMap[policy.timeframe] || '24 hours';
    const refund = refundMap[policy.refundPercentage] || 'full refund';
    return `Clients must cancel ${timeframe} in advance for a ${refund}.`;
  };

  // Generate is itself an explicit user action (single button click), so save once here.
  // The user then reviews/edits in the editor view and clicks Save again to persist edits.
  const handleGenerate = async () => {
    const { generateTermsOfService } = require('../../utils/policyTemplates');

    const practitionerData = {
      displayName: state.draft.profile.displayName,
      email: state.draft.contactFooter.email,
      cancellationPolicy: state.draft.contactFooter.cancellationPolicy,
    };

    const generatedContent = generateTermsOfService(answers, practitionerData);

    try {
      await actions.updateContactFooter('termsOfService', {
        answers,
        content: generatedContent,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      });
      toast.success('Generated', 'Terms of service generated. Review and edit before publishing.');
    } catch (err) {
      console.error('[policies] Terms generate failed:', err);
      toast.error('Generation failed', 'Could not save the generated terms. Please try again.');
    }
  };

  return (
    <div className="p-6 space-y-6">
      {/* Back Button */}
      <button
        onClick={onBack}
        className="flex items-center gap-2 text-sm text-gray-600 hover:text-purple-600 transition-colors"
      >
        <ChevronRight className="w-4 h-4 rotate-180" />
        <span>Back to Policies</span>
      </button>

      {/* Header */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Terms of Service</h3>
        <p className="text-sm text-gray-600">
          Answer a few questions about your practice and we'll generate Terms of Service you can customize.
        </p>
      </div>

      {/* Disclaimer */}
      <PolicyDisclaimer />

      {/* Questions */}
      <div className="space-y-4">
        {/* Q1: Service Types */}
        <QuestionCard
          question="What types of services do you offer?"
          helperText="We've pulled this from your services list"
          autoDetect={serviceNames.length > 0}
          autoDetectReason={serviceNames.length > 0 ? `Auto-detected: ${serviceNames.length} service${serviceNames.length !== 1 ? 's' : ''} found` : ''}
          fullWidthControl
        >
          <textarea
            value={answers.serviceTypes}
            onChange={(e) => handleTextChange('serviceTypes', e.target.value)}
            placeholder="e.g., Tarot Reading, Energy Healing, Spiritual Coaching"
            rows={2}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
          />
        </QuestionCard>

        {/* Q2: Cancellation Policy */}
        <QuestionCard
          question="What is your cancellation policy?"
          helperText="This is pulled from your Cancellation Policy — no need to re-enter"
          autoDetect={hasCancellationPolicy}
          autoDetectReason={hasCancellationPolicy ? 'Auto-detected: Cancellation Policy exists' : ''}
          fullWidthControl
        >
          {hasCancellationPolicy ? (
            <div className="p-3 bg-gray-50 rounded-lg border border-gray-200 text-sm text-gray-700">
              {getCancellationSummary(cancellationPolicy)}
            </div>
          ) : (
            <div className="p-3 bg-amber-50 rounded-lg border border-amber-200 text-sm text-amber-800">
              No cancellation policy set up yet. A default clause will be included.
            </div>
          )}
        </QuestionCard>

        {/* Q3: Health/Medical Disclaimer */}
        <QuestionCard
          question="Do your services involve health, medical, or mental health guidance?"
          helperText="Recommended for energy healers, coaches, readers, and anyone whose work touches on wellness or personal guidance. If you're unsure, it's safest to include this."
        >
          <Toggle
            enabled={answers.healthMedicalDisclaimer}
            onChange={() => handleToggle('healthMedicalDisclaimer')}
          />
        </QuestionCard>

        {/* Q4: Financial/Legal Disclaimer */}
        <QuestionCard
          question="Do you provide financial or legal advice?"
          helperText="Only needed if you offer business coaching, financial guidance, or legal-adjacent advice. Most spiritual practitioners can leave this off."
        >
          <Toggle
            enabled={answers.financialLegalDisclaimer}
            onChange={() => handleToggle('financialLegalDisclaimer')}
          />
        </QuestionCard>

        {/* Q5: Minimum Age */}
        <QuestionCard
          question="What is the minimum age for your clients?"
          helperText="Age requirements help set clear expectations and determine what consent you'll need to collect."
          fullWidthControl
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {[
              {
                value: '18+',
                label: '18+ (Adults only)',
                description: 'Clients must be 18 or older to book. Most common for spiritual and wellness practitioners.',
              },
              {
                value: '16+',
                label: '16+ with guardian consent',
                description: 'Teens 16+ can book with written consent from a parent or legal guardian. You will need to collect consent forms.',
              },
              {
                value: 'none',
                label: 'No minimum age',
                description: 'All ages welcome with appropriate guardian consent for minors. Best if you specialize in family or youth work.',
              },
            ].map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => handleSelectChange('minimumAge', option.value)}
                style={{
                  padding: '12px 14px',
                  borderRadius: '8px',
                  border: answers.minimumAge === option.value ? '2px solid #7c3aed' : '2px solid #E5E7EB',
                  backgroundColor: answers.minimumAge === option.value ? '#F5F3FF' : '#FFFFFF',
                  cursor: 'pointer',
                  transition: 'all 0.15s ease',
                  textAlign: 'left',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '2px',
                }}
              >
                <span style={{ fontSize: '13px', fontWeight: '600', color: '#111827' }}>
                  {option.label}
                </span>
                <span style={{ fontSize: '11px', fontWeight: '400', color: '#6B7280', lineHeight: '1.4' }}>
                  {option.description}
                </span>
              </button>
            ))}
          </div>
          <div style={{
            marginTop: '8px',
            padding: '8px 12px',
            backgroundColor: '#F0EBFF',
            borderRadius: '6px',
            display: 'flex',
            alignItems: 'flex-start',
            gap: '6px',
          }}>
            <span style={{ fontSize: '11px', color: '#6D28D9', fontWeight: '500', lineHeight: '1.4' }}>
              💡 Most practitioners choose 18+ to keep things simple and avoid consent paperwork.
            </span>
          </div>
        </QuestionCard>

        {/* Q6: Packages */}
        <QuestionCard
          question="Do you offer gift cards or pre-paid packages?"
          helperText="Adds terms for refunds and expiration of pre-paid services"
        >
          <Toggle
            enabled={answers.offersPackages}
            onChange={() => handleToggle('offersPackages')}
          />
        </QuestionCard>

        {/* Q7: Dispute Resolution */}
        <QuestionCard
          question="How should disputes be handled?"
          helperText="If a client ever has a complaint or disagreement about your services, this determines how it gets resolved."
          fullWidthControl
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {[
              {
                value: 'mediation',
                label: 'Mediation',
                description: 'Both sides talk it out with a neutral third party. Low cost, collaborative. Best for most practitioners.',
              },
              {
                value: 'arbitration',
                label: 'Arbitration',
                description: 'A private judge makes a binding decision. More formal than mediation but avoids court.',
              },
              {
                value: 'small_claims',
                label: 'Small claims court',
                description: 'Disputes go to your local small claims court. Simple and public, no lawyer needed.',
              },
            ].map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => handleSelectChange('disputeResolution', option.value)}
                style={{
                  padding: '12px 14px',
                  borderRadius: '8px',
                  border: answers.disputeResolution === option.value ? '2px solid #7c3aed' : '2px solid #E5E7EB',
                  backgroundColor: answers.disputeResolution === option.value ? '#F5F3FF' : '#FFFFFF',
                  cursor: 'pointer',
                  transition: 'all 0.15s ease',
                  textAlign: 'left',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '2px',
                }}
              >
                <span style={{ fontSize: '13px', fontWeight: '600', color: '#111827' }}>
                  {option.label}
                </span>
                <span style={{ fontSize: '11px', fontWeight: '400', color: '#6B7280', lineHeight: '1.4' }}>
                  {option.description}
                </span>
              </button>
            ))}
          </div>

          {/* Recommendation callout */}
          <div style={{
            marginTop: '8px',
            padding: '8px 12px',
            backgroundColor: '#F0EBFF',
            borderRadius: '6px',
            display: 'flex',
            alignItems: 'flex-start',
            gap: '6px',
          }}>
            <span style={{ fontSize: '11px', color: '#6D28D9', fontWeight: '500', lineHeight: '1.4' }}>
              💡 Mediation is the most common choice for service-based practitioners. It's the least adversarial and keeps things friendly.
            </span>
          </div>
        </QuestionCard>
      </div>

      {/* Generate Button */}
      <button
        onClick={handleGenerate}
        className="w-full py-3 text-white font-semibold rounded-lg transition-colors"
        style={{ backgroundColor: '#7c3aed' }}
      >
        Generate Terms of Service
      </button>
    </div>
  );
};

// Editor View Component
const TermsOfServiceEditor = ({ onBack, onDirtyChange }) => {
  const { state, actions } = useWebsiteBuilder();
  const termsOfService = state.draft.contactFooter.termsOfService;
  const [showRegenerateConfirm, setShowRegenerateConfirm] = useState(false);
  const [content, setContent] = useState(termsOfService?.content || '');
  const toast = useToast();
  const [isDirty, setIsDirty] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Propagate local isDirty up to PoliciesPanel — see DEF-036.
  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty, onDirtyChange]);

  // Flush-on-unmount: reset wrapper's tracked dirty state when this editor unmounts.
  useEffect(() => {
    return () => onDirtyChange?.(false);
  }, [onDirtyChange]);

  const handleContentChange = (html) => {
    setContent(html);
    setIsDirty(true);
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await actions.updateContactFooter('termsOfService', {
        ...termsOfService,
        content,
        updatedAt: new Date().toISOString(),
      });
      setIsDirty(false);
      toast.success('Saved', 'Terms of service saved.');
    } catch (err) {
      console.error('[policies] Terms save failed:', err);
      toast.error('Save failed', 'Your terms of service could not be saved. Please check your connection and try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleRegenerate = () => {
    // Clear local content; transition back to Q&A flow happens because termsOfService.content
    // is empty. Don't save here — user must Save explicitly after re-generating.
    setContent('');
    setIsDirty(false);
    setShowRegenerateConfirm(false);
  };

  const handleBack = () => {
    if (isDirty) {
      const confirmed = window.confirm('You have unsaved changes. Discard?');
      if (!confirmed) return;
    }
    onBack();
  };

  return (
    <div className="p-6 space-y-6">
      {/* Back Button */}
      <button
        onClick={handleBack}
        className="flex items-center gap-2 text-sm text-gray-600 hover:text-purple-600 transition-colors"
      >
        <ChevronRight className="w-4 h-4 rotate-180" />
        <span>Back to Policies</span>
      </button>

      {/* Header */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Terms of Service</h3>
      </div>

      {/* Disclaimer */}
      <PolicyDisclaimer />

      {/* Editor */}
      <PolicyEditor
        content={content}
        onChange={handleContentChange}
        placeholder="Your terms of service..."
        minHeight="500px"
      />
      <p className="text-xs text-gray-500 mt-2">
        Edits require clicking Save to persist.
      </p>

      {/* Bottom Actions */}
      <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200">
        <button
          onClick={() => setShowRegenerateConfirm(true)}
          className="text-sm text-gray-600 hover:text-purple-600 transition-colors"
        >
          Regenerate
        </button>
        <LoadingButton
          onClick={handleSave}
          loading={isSaving}
          loadingText="Saving..."
          disabled={!isDirty}
          className="px-6 py-2.5 bg-purple-600 text-white font-semibold rounded-lg hover:bg-purple-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
        >
          Save Terms of Service
        </LoadingButton>
      </div>

      {/* Regenerate Confirmation Modal */}
      {showRegenerateConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setShowRegenerateConfirm(false)}>
          <div className="bg-white rounded-xl p-6 max-w-md" onClick={(e) => e.stopPropagation()}>
            <h4 className="text-lg font-semibold text-gray-900 mb-2">Regenerate Terms of Service?</h4>
            <p className="text-sm text-gray-600 mb-6">
              This will replace your current Terms of Service with a new template. Your edits will be lost. Continue?
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowRegenerateConfirm(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleRegenerate}
                className="px-4 py-2 text-sm font-medium text-white rounded-lg"
                style={{ backgroundColor: '#7c3aed' }}
              >
                Regenerate
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PoliciesSettings;
